document.addEventListener('DOMContentLoaded', () => {
  // Close open localization disclosures when clicking outside or pressing Escape.
  // Avoid targeting generic accordions/collapsibles so they remain independently interactive.
  const DISCLOSURE_SELECTOR = 'details[data-localization-disclosure]';

  document.addEventListener('click', (event) => {
    document.querySelectorAll(`${DISCLOSURE_SELECTOR}[open]`).forEach((details) => {
      if (!details.contains(event.target)) {
        details.removeAttribute('open');
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll(`${DISCLOSURE_SELECTOR}[open]`).forEach((details) => {
        details.removeAttribute('open');
      });
    }
  });

  // Mobile menu drawer toggle.
  const mobileMenu = document.getElementById('MobileMenu');
  const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
  if (mobileMenu && mobileMenuToggle) {
    const updateMenuState = () => {
      const isOpen = mobileMenu.classList.contains('is-open');
      mobileMenuToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (!isOpen) mobileMenuToggle.focus();
    };

    const menuObserver = new MutationObserver(updateMenuState);
    menuObserver.observe(mobileMenu, { attributes: true, attributeFilter: ['class'] });

    mobileMenuToggle.addEventListener('click', () => {
      if (mobileMenu.classList.contains('is-open')) {
        mobileMenu.close();
      } else {
        mobileMenu.open();
        mobileMenu.querySelector('[data-modal-close]')?.focus();
      }
    });

    // Mobile submenu accordion toggles (delegated so section re-renders keep working).
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('#MobileMenu [data-submenu-toggle]');
      if (!toggle) return;

      const parentItem = toggle.closest('li');
      if (!parentItem) return;

      const isOpen = parentItem.classList.contains('is-open');
      parentItem.classList.toggle('is-open', !isOpen);
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  }

  // Predictive search panel toggle (drawer-style, inline under the header).
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchPanel = document.querySelector('[data-search-panel]');
  if (searchToggle && searchPanel) {
    searchToggle.addEventListener('click', () => {
      const isHidden = searchPanel.hasAttribute('hidden');
      if (isHidden) {
        searchPanel.removeAttribute('hidden');
        searchToggle.setAttribute('aria-expanded', 'true');
        searchPanel.querySelector('input')?.focus();
      } else {
        searchPanel.setAttribute('hidden', '');
        searchToggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // Predictive search modal toggle (centered overlay).
  const searchModalToggle = document.querySelector('[data-search-modal-toggle]');
  const searchModal = document.getElementById('HeaderSearchModal');
  if (searchModalToggle && searchModal) {
    searchModalToggle.addEventListener('click', () => {
      customElements.whenDefined('dialog-modal').then(() => {
        searchModal.open();
        searchModal.querySelector('input')?.focus();
      });
    });
  }

  // Sticky header: behavior depends on the merchant's "Sticky behavior" setting.
  // - "always": header stays pinned via CSS `position: sticky`, no JS needed beyond the scroll shadow.
  // - "scroll-up": header hides on scroll down, reappears on scroll up.
  // - "none": header scrolls away normally (no listener attached).
  //
  // Important: in Shopify, the actual sticky positioning must be applied to the
  // auto-generated outer section wrapper (`.section-header` in the schema), not
  // to the inner hand-written `<header>` element. This mirrors the Dawn
  // approach and avoids a known Safari/WebKit bug where sticky breaks on nested
  // elements with their own stacking/positioning context.
  //
  // Sticky background activation is now based on the distance between the
  // sticky header and the next content section, with a configurable (and
  // responsive) early-activation offset.
  let lastScroll = 0;
  let nextSection = null;

  function getHeaderElements() {
    const header = document.querySelector('[data-site-header]');
    const headerSection = header?.closest('.section-header');
    return { header, headerSection };
  }

  function getActivationOffset(header) {
    if (!header) return 0;
    const mode = header.dataset.stickyActivation || 'default';
    if (mode === 'early') return parseInt(header.dataset.stickyEarlyOffset || '100', 10);
    if (mode === 'custom') {
      const width = window.innerWidth;
      if (width <= 749) return parseInt(header.dataset.stickyOffsetMobile || '0', 10);
      if (width <= 989) return parseInt(header.dataset.stickyOffsetTablet || '0', 10);
      return parseInt(header.dataset.stickyOffsetDesktop || '0', 10);
    }
    return 0;
  }

  function findNextSection(header, headerSection) {
    if (!header || !headerSection) return null;
    const headerBottom = header.getBoundingClientRect().bottom;
    return [...document.querySelectorAll('.shopify-section')].find((section) => {
      if (section === headerSection) return false;
      if (section.classList.contains('section-announcement-bar')) return false;
      if (headerSection.contains(section)) return false;
      return section.getBoundingClientRect().top > headerBottom;
    });
  }

  function scrolledPastHeroThreshold(transparent, fullScreenHero) {
    if (!transparent || !fullScreenHero) return 10;
    return fullScreenHero.offsetTop + fullScreenHero.clientHeight;
  }

  function updateStickyHeader() {
    const { header, headerSection } = getHeaderElements();
    if (!header || !headerSection || header.dataset.stickyBehavior === 'none') return;

    headerSection.classList.add('section-header--sticky');

    const hideOnScrollDown = header.dataset.stickyBehavior === 'scroll-up';
    const transparent = header.classList.contains('site-header--transparent');
    const fullScreenHero = document.querySelector('.hero--height-full-screen');
    const current = window.scrollY;
    const heroThreshold = scrolledPastHeroThreshold(transparent, fullScreenHero);

    if (current > 20 && current <= heroThreshold) headerSection.classList.add('scrolled-within-hero');
    else headerSection.classList.remove('scrolled-within-hero');
    if (current > heroThreshold) headerSection.classList.add('scrolled-past-hero');
    else headerSection.classList.remove('scrolled-past-hero');

    const mode = header.dataset.stickyActivation || 'default';
    const offset = getActivationOffset(header);
    const headerBottom = header.getBoundingClientRect().bottom;
    let shouldActivate = false;

    if (mode === 'default') {
      // Default now activates as soon as the page has been scrolled.
      shouldActivate = current > 0;
    } else if (nextSection) {
      const distance = nextSection.getBoundingClientRect().top - headerBottom;
      shouldActivate = distance <= offset;
    } else if (transparent && fullScreenHero) {
      // Fallback: activate when the hero bottom is within the offset of the header.
      const heroBottom = fullScreenHero.offsetTop + fullScreenHero.clientHeight;
      shouldActivate = current + headerBottom >= heroBottom - offset;
    }

    headerSection.classList.toggle('scrolled-near-section', shouldActivate);

    if (hideOnScrollDown) {
      if (current > 80) {
        if (current > lastScroll) {
          headerSection.classList.add('is-hidden');
          // Close mobile menu / search drawers so they don't stay open while the header slides away.
          if (mobileMenu && !mobileMenu.hidden) mobileMenu.close();
          if (searchPanel && !searchPanel.hasAttribute('hidden')) {
            searchPanel.setAttribute('hidden', '');
            searchToggle?.setAttribute('aria-expanded', 'false');
          }
          if (searchModal && typeof searchModal.close === 'function') searchModal.close();
        } else {
          headerSection.classList.remove('is-hidden');
        }
      } else {
        headerSection.classList.remove('is-hidden');
      }
    }
    lastScroll = current;
  }

  function initStickyHeader() {
    const { header, headerSection } = getHeaderElements();
    if (!header || !headerSection) return;
    nextSection = findNextSection(header, headerSection);
    updateStickyHeader();
  }

  initStickyHeader();
  window.addEventListener('scroll', updateStickyHeader, { passive: true });
  window.addEventListener('resize', updateStickyHeader, { passive: true });
  document.addEventListener('shopify:section:load', initStickyHeader);
});
